import { Calendar, CheckCircle2, Clock } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

import { MaintenanceAction } from './types';

interface Props {
    maintenanceActions?: MaintenanceAction[];
}

export function LeftSidebar({ maintenanceActions }: Props) {
    // #TODO: Replace hardcoded maintenance actions with real data from backend API
    const defaultMaintenanceActions: MaintenanceAction[] = [
        {
            id: 'ma-001',
            actionNumber: 'MA-2025-001',
            title: 'C-Check Inspection',
            status: 'in-progress',
            startDate: '10-Oct-25',
            completionDate: '20-Nov-25',
            assignedTo: 'MRO Team',
            progress: 65,
            actionType: 'check',
        },
        {
            id: 'ma-002',
            actionNumber: 'MA-2025-002',
            title: 'Engine #1 Replacement',
            status: 'completed',
            startDate: '01-Sep-25',
            completionDate: '15-Sep-25',
            assignedTo: 'Engine Shop',
            progress: 100,
            actionType: 'replacement',
        },
        {
            id: 'ma-003',
            actionNumber: 'MA-2025-003',
            title: 'Landing Gear Overhaul',
            status: 'in-progress',
            startDate: '15-Oct-25',
            completionDate: '10-Nov-25',
            assignedTo: 'MRO Team',
            progress: 45,
            actionType: 'repair',
        },
        {
            id: 'ma-004',
            actionNumber: 'MA-2025-004',
            title: 'APU Inspection',
            status: 'scheduled',
            startDate: '01-Nov-25',
            completionDate: '05-Nov-25',
            assignedTo: 'APU Specialist',
            progress: 0,
            actionType: 'inspection',
        },
        {
            id: 'ma-005',
            actionNumber: 'MA-2025-005',
            title: 'Cabin Modification',
            status: 'in-progress',
            startDate: '20-Oct-25',
            completionDate: '15-Nov-25',
            assignedTo: 'Cabin Team',
            progress: 30,
            actionType: 'modification',
        },
    ];

    const actionsToRender = maintenanceActions || defaultMaintenanceActions;

    return (
        <div className="w-96 border-r bg-muted/50 flex-shrink-0">
            <div className="h-14 border-b px-4 flex items-center justify-between sticky top-16 bg-muted/50 z-10">
                <h2 className="font-semibold text-foreground">
                    Maintenance Planning
                </h2>
            </div>
            <div className="p-3 space-y-3 max-h-[calc(100vh-120px)] overflow-y-auto">
                {actionsToRender.map((action, idx) => (
                    <Card
                        key={action.id}
                        className="bg-card hover:shadow-md transition-all cursor-pointer"
                    >
                        <CardContent className="p-3 space-y-3">
                            <div>
                                <div className="flex items-start justify-between mb-1">
                                    <div className="font-mono text-xs text-muted-foreground">
                                        {action.actionNumber}
                                    </div>
                                    <Badge
                                        variant="outline"
                                        className={`text-[10px] ${
                                            action.status === 'completed'
                                                ? 'bg-green-100 text-green-700 border-green-200'
                                                : action.status ===
                                                    'in-progress'
                                                  ? 'bg-primary/10 text-primary border-primary/20'
                                                  : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                        }`}
                                    >
                                        {action.status === 'completed' ? (
                                            <>
                                                <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />
                                                Completed
                                            </>
                                        ) : action.status === 'in-progress' ? (
                                            <>
                                                <Clock className="h-2.5 w-2.5 mr-0.5" />
                                                In Progress
                                            </>
                                        ) : (
                                            <>
                                                <Calendar className="h-2.5 w-2.5 mr-0.5" />
                                                Scheduled
                                            </>
                                        )}
                                    </Badge>
                                </div>
                                <div className="text-sm font-semibold text-card-foreground mb-2">
                                    {action.title}
                                </div>
                            </div>

                            <div className="space-y-1.5 text-xs">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">
                                        Start:
                                    </span>
                                    <span className="text-card-foreground font-medium">
                                        {action.startDate}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">
                                        Complete:
                                    </span>
                                    <span className="text-card-foreground font-medium">
                                        {action.completionDate}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <Badge
                                    variant="outline"
                                    className="text-[10px]"
                                >
                                    {action.assignedTo}
                                </Badge>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs text-muted-foreground">
                                        Progress
                                    </span>
                                    <span className="text-xs font-semibold text-card-foreground">
                                        {action.progress}%
                                    </span>
                                </div>
                                <Progress
                                    value={action.progress}
                                    className="h-1.5"
                                />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}
